import Page from "components/common/ui/Page/Page";
import BaseForm from "components/common/form/Form/Form";
import { InputText, InputEmail } from "components/common/form/Input/Input";
import InputSelect from "components/common/form/InputSelect/InputSelect";
import Button from "components/common/ui/Button/Button";
import Alert, { AlertType } from "components/common/ui/Alert/Alert";
import {
  ButtonContainer,
  StyledContainer,
  StyledContent,
  StyledOverlay,
  StyledWrapper,
} from "components/cart/CartErrorModal/CartErrorModal.Styled";
import { Portal } from "react-portal";
import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { PageType } from "ts/enums";
import useCart from "hooks/cart/useCart";
import { useSelector } from "redux/hooks";
import { useDispatch } from "redux/hooks";
import { updateCart } from "redux/cart/cartSlice";
import { setAppCookie } from "utilities/cookies";
import { getCartCookieExpirationDate } from "behavior/getCartCookieExpiration";
import { COOKIE_CART_ID } from "utilities/consts";
import {
  useCreateCartWithGiftCertificateMutation,
  useAddGiftCertificateToCartMutation,
  useGetGiftCertificateValuesQuery,
} from "app/api/giftCertificate/giftCertificateApi";
import Typography from "components/common/ui/Typography/Typography";
import { ContentWrapper, HeroBanner, HeroTitle, HeroDescription, FieldGroup } from "./GiftCertificate.Styled";
import styled from "styled-components";
import GenericErrorPage from "pages/error/GenericErrorPage";

const ModalButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
`;

interface GiftCertificateFormValues {
  to_name: string;
  to_email: string;
  from_name: string;
  from_email: string;
  amount: number;
  message: string;
}

const GiftCertificatePage: FunctionComponent = () => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<GiftCertificateFormValues>();

  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingData, setPendingData] = useState<GiftCertificateFormValues | null>(null);

  const dispatch = useDispatch();
  const { cart } = useSelector((state) => state);
  const bcStore = useSelector((state) => state.bcStore.store);
  const authInitialized = useSelector((state) => state.auth.authInitialized);
  const { deleteCart } = useCart();
  const customerId = useSelector((state) => state.auth.customer?.id);

  const { data: amountOptions = [] } = useGetGiftCertificateValuesQuery(undefined, { skip: !bcStore || !authInitialized });

  const [createCartWithGC] = useCreateCartWithGiftCertificateMutation();
  const [addGCToCart] = useAddGiftCertificateToCartMutation();

  const submitGiftCertificate = async (data: GiftCertificateFormValues, forceCreate = false) => {
    setIsLoading(true);

    const giftCertificate = {
      name: "Gift Certificate",
      theme: "General",
      amount: data.amount,
      message: data.message,
      quantity: 1,
      recipient: { email: data.to_email, name: data.to_name },
      sender: { email: data.from_email, name: data.from_name },
    };

    try {
      let updatedCart;

      if (!cart.cart?.id || forceCreate) {
        updatedCart = await createCartWithGC({
          channelId: bcStore!.embeddedCheckoutChannelId,
          giftCertificate,
        }).unwrap();

        await setAppCookie(
          COOKIE_CART_ID,
          { storeHash: bcStore!.hash, id: updatedCart.id, customer: updatedCart.customer_id },
          getCartCookieExpirationDate,
          customerId,
        );
      } else {
        updatedCart = await addGCToCart({
          cartId: cart.cart.id,
          giftCertificate,
        }).unwrap();
      }

      dispatch(updateCart(updatedCart));
      setErrorMsg("");
      setSuccess(true);
    } catch (e) {
      console.error(e);
      setSuccess(false);
      setErrorMsg(t("giftCard.submitError") as string);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit: SubmitHandler<GiftCertificateFormValues> = async (data) => {
    setSuccess(false);

    const hasGiftCertificate = (cart.cart?.line_items?.gift_certificates?.length ?? 0) > 0;

    if (hasGiftCertificate) {
      setErrorMsg(t("giftCard.onlyOneAllowed") as string);
      return;
    }

    const hasPhysicalItems = (cart.cart?.line_items?.physical_items?.length ?? 0) > 0;

    if (hasPhysicalItems) {
      setPendingData(data);
      setShowConfirm(true);
      return;
    }

    setErrorMsg("");
    await submitGiftCertificate(data);
  };

  const handleConfirm = async () => {
    setShowConfirm(false);
    await deleteCart();
    if (pendingData) await submitGiftCertificate(pendingData, true);
  };

  if (!bcStore?.giftCertificateAvailable) {
    return <GenericErrorPage />;
  }

  return (
    <Page pageType={PageType.PAGE} title={t("giftCard.title").toString()}>
      <HeroBanner>
        <HeroTitle>{t("giftCard.title")}</HeroTitle>
        <HeroDescription>{t("giftCard.page_description")}</HeroDescription>
      </HeroBanner>
      <ContentWrapper>
        {success && (
          <Alert type={AlertType.SUCCESS} messages={[t("giftCard.createdSuccess").toString()]} />
        )}
        {errorMsg && (
          <Alert type={AlertType.ERROR} messages={[errorMsg]} />
        )}
        {showConfirm && (
          <Portal node={document && document.getElementById("portal__modal")}>
            <StyledWrapper>
              <StyledOverlay>
                <StyledContainer>
                  <StyledContent>
                    <Typography variant="body">{t("giftCard.cantAddGiftCard")}</Typography>
                    <ModalButtons>
                      <Button variant="primary_01" fullWidth onClick={handleConfirm} loading={isLoading}>
                        {t("giftCard.removeItems")}
                      </Button>
                      <Button variant="primary_03" fullWidth onClick={() => setShowConfirm(false)}>
                        {t("cancel")}
                      </Button>
                    </ModalButtons>
                  </StyledContent>
                </StyledContainer>
              </StyledOverlay>
            </StyledWrapper>
          </Portal>
        )}
        <BaseForm onSubmit={handleSubmit(onSubmit)}>
          <FieldGroup>
            <InputText
              label={t("giftCard.recipientName") as string}
              required
              {...register("to_name", { required: t("giftCard.errors.toName") as string })}
              errorMsg={errors.to_name?.message}
            />
            <InputEmail
              label={t("giftCard.recipientEmail") as string}
              required
              {...register("to_email", { required: t("giftCard.errors.toMail") as string })}
              errorMsg={errors.to_email?.message}
            />
            <InputText
              label={t("giftCard.yourName") as string}
              required
              {...register("from_name", { required: t("giftCard.errors.yourName") as string })}
              errorMsg={errors.from_name?.message}
            />
            <InputEmail
              label={t("giftCard.yourEmail") as string}
              required
              {...register("from_email", { required: t("giftCard.errors.yourEmail") as string })}
              errorMsg={errors.from_email?.message}
            />
            <Controller
              name="amount"
              control={control}
              rules={{ required: t("giftCard.selectAmount") as string }}
              render={({ field }) => (
                <InputSelect
                  label={t("giftCard.amount") as string}
                  required
                  options={amountOptions}
                  value={field.value}
                  onChange={field.onChange}
                  errorMsg={errors.amount?.message}
                />
              )}
            />
            <InputText
              label={t("giftCard.message") as string}
              {...register("message")}
              errorMsg={errors.message?.message}
            />
          </FieldGroup>
          <Button variant="primary_01" type="submit" fullWidth loading={isLoading} disabled={isLoading}>
            {t("addToCart.giftCard")}
          </Button>
        </BaseForm>
      </ContentWrapper>
    </Page>
  );
};

export default GiftCertificatePage;
